import React, { useEffect, useState } from 'react';
import { 
  Grid, 
  Paper, 
  Typography, 
  Box, 
  Divider,
  Button,
  useTheme,
  LinearProgress,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import { dashboardService } from '../services/dashboardService';
import { MetricCard } from '../components/dashboard/MetricCard';
import { SystemStatusCard } from '../components/dashboard/SystemStatusCard';
import { LineChart } from '../components/charts/LineChart';
import { useWebSocket } from '../services/webSocketService';

interface ActivityEvent {
  id: string;
  type: 'info' | 'warning' | 'error';
  message: string;
  timestamp: string;
}

const DashboardHome: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [modelAccuracy, setModelAccuracy] = useState(76.8);
  const [systemUptime, setSystemUptime] = useState(12.5);
  const [activeWorkers, setActiveWorkers] = useState(8);
  const [pendingTasks, setPendingTasks] = useState(25);
  const [apiHealth, setApiHealth] = useState(130);
  const [dbPerformance, setDbPerformance] = useState(65);
  const [activities, setActivities] = useState<ActivityEvent[]>([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Use WebSocket for real-time updates
  const { data: resourceData, isConnected } = useWebSocket<any>('system_resources');
  
  useEffect(() => {
    fetchDashboardData();
    
    // Set up polling for dashboard data
    const interval = setInterval(() => {
      fetchDashboardData();
    }, 60000); // Refresh every minute
    
    return () => clearInterval(interval);
  }, []);
  
  useEffect(() => {
    if (resourceData) {
      setModelAccuracy(resourceData.model_accuracy || modelAccuracy);
      setSystemUptime(resourceData.system_uptime || systemUptime);
      setActiveWorkers(resourceData.active_workers || activeWorkers);
      setPendingTasks(resourceData.pending_tasks || pendingTasks);
      setApiHealth(resourceData.api_health || apiHealth);
      setDbPerformance(resourceData.db_performance || dbPerformance);
      setLastUpdated(new Date());
    }
  }, [resourceData]);
  
  const fetchDashboardData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Fetch system metrics
      const metricsData = await dashboardService.getSystemHealth();
      
      // Fetch model performance data
      const performanceData = await dashboardService.getModelPerformance();
      
      // Fetch recent activities
      const activitiesData = await dashboardService.getLivePredictions();
      
      // Update state with fetched data
      setModelAccuracy(performanceData.overall_accuracy);
      
      // Update activities
      const formattedActivities: ActivityEvent[] = activitiesData.predictions.map((pred: any) => ({
        id: pred.id,
        type: pred.value ? 'info' : 'warning',
        message: `${pred.match}: ${pred.prediction} (${pred.confidence.toFixed(1)}%)`,
        timestamp: pred.kickoff
      }));
      
      setActivities(formattedActivities);
      setLastUpdated(new Date());
    } catch (err) {
      setError('Failed to fetch dashboard data');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleRefresh = () => {
    fetchDashboardData();
  };
  
  const getStatusIcon = (type: string) => {
    switch (type) {
      case 'info':
        return <CheckCircleIcon color="success" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      case 'error':
        return <ErrorIcon color="error" />;
      default:
        return <CheckCircleIcon color="success" />;
    }
  };
  
  // Performance chart data
  const performanceChartData = {
    labels: ['Mar 24', 'Mar 25', 'Mar 26', 'Mar 27', 'Mar 28', 'Mar 29', 'Mar 30'],
    datasets: [
      {
        label: 'Model Accuracy',
        data: [75.2, 76.5, 74.8, 77.1, 78.3, 76.9, modelAccuracy],
        borderColor: theme.palette.primary.main,
        fill: true
      }
    ]
  };
  
  // API response time chart data
  const apiResponseChartData = {
    labels: ['Mar 24', 'Mar 25', 'Mar 26', 'Mar 27', 'Mar 28', 'Mar 29', 'Mar 30'],
    datasets: [
      {
        label: 'API Response Time (ms)',
        data: [145, 152, 165, 143, 138, 132, apiHealth],
        borderColor: theme.palette.secondary.main,
        fill: true
      }
    ]
  };
  
  return (
    <Box sx={{ width: '100%' }}>
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          mb: 3, 
          alignItems: 'center',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 2 : 0
        }}
      >
        <Typography variant="h5" component="h1">
          System Dashboard
        </Typography>
        
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center',
            width: isMobile ? '100%' : 'auto',
            justifyContent: isMobile ? 'space-between' : 'flex-end'
          }}
        >
          <Chip 
            label={isConnected ? 'Real-time: Active' : 'Real-time: Inactive'} 
            color={isConnected ? 'success' : 'error'} 
            size="small" 
            sx={{ mr: 2 }} 
          />
          
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ mr: 2, display: { xs: 'none', sm: 'block' } }}
          >
            Last updated: {lastUpdated.toLocaleTimeString()}
          </Typography>
          
          <Button 
            variant="outlined" 
            startIcon={<RefreshIcon />} 
            onClick={handleRefresh}
            disabled={isLoading}
            size={isMobile ? "small" : "medium"}
          >
            Refresh
          </Button>
        </Box>
      </Box>
      
      {isLoading && <LinearProgress sx={{ mb: 2 }} />}
      
      <Grid container spacing={3}>
        {/* System Metrics Row */}
        <Grid item xs={12} md={8}>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={6} lg={4}>
              <MetricCard 
                title="System Uptime"
                value={systemUptime}
                unit="days"
                status="success"
                icon="cpu"
              />
            </Grid>
            
            <Grid item xs={6} sm={6} lg={4}>
              <MetricCard 
                title="Model Accuracy"
                value={modelAccuracy}
                unit="%"
                status={modelAccuracy > 80 ? 'success' : modelAccuracy > 70 ? 'warning' : 'error'}
                icon="accuracy"
                change={-1.2}
              />
            </Grid>
            
            <Grid item xs={6} sm={6} lg={4}>
              <MetricCard 
                title="Active Workers"
                value={activeWorkers}
                unit="nodes"
                status="success"
                icon="users"
              />
            </Grid>
            
            <Grid item xs={6} sm={6} lg={4}>
              <MetricCard 
                title="Pending Tasks"
                value={pendingTasks}
                unit="tasks"
                status={pendingTasks > 50 ? 'error' : pendingTasks > 20 ? 'warning' : 'success'}
                icon="custom"
                customIcon={<RefreshIcon fontSize="large" />}
                change={+5}
              />
            </Grid>
            
            <Grid item xs={6} sm={6} lg={4}>
              <MetricCard 
                title="API Response"
                value={apiHealth}
                unit="ms"
                status={apiHealth > 200 ? 'error' : apiHealth > 150 ? 'warning' : 'success'}
                icon="api"
                change={-10}
              />
            </Grid>
            
            <Grid item xs={6} sm={6} lg={4}>
              <MetricCard 
                title="DB Performance"
                value={dbPerformance}
                unit="ms"
                status={dbPerformance > 80 ? 'error' : dbPerformance > 50 ? 'warning' : 'success'}
                icon="memory"
                change={+15}
              />
            </Grid>
          </Grid>
        </Grid>
        
        {/* System Status Card */}
        <Grid item xs={12} md={4}>
          <SystemStatusCard 
            systemStatus="Online"
            components={[
              { name: "API Service", status: "healthy", responseTime: "125ms" },
              { name: "Database", status: "warning", responseTime: "65ms" },
              { name: "ML Prediction Engine", status: "healthy", responseTime: "340ms" },
              { name: "Data Processing", status: "healthy", responseTime: "215ms" },
              { name: "External Feeds", status: "error", responseTime: "3250ms" }
            ]}
            lastUpdated={lastUpdated}
          />
        </Grid>
        
        {/* Charts Row */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Model Accuracy</Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ height: 300 }}>
              <LineChart 
                labels={performanceChartData.labels}
                datasets={performanceChartData.datasets}
                height={300}
                showLegend={!isMobile}
              />
            </Box>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>API Response Time</Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ height: 300 }}>
              <LineChart 
                labels={apiResponseChartData.labels}
                datasets={apiResponseChartData.datasets}
                height={300}
                showLegend={!isMobile}
              />
            </Box>
          </Paper>
        </Grid>
        
        {/* Activity Feed */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, height: '100%', borderRadius: 2, boxShadow: 2 }}>
            <Typography variant="h6">Latest Predictions</Typography>
            <Divider sx={{ my: 1 }} />
            
            <List sx={{ maxHeight: 300, overflow: 'auto' }}>
              {activities.map((activity) => (
                <ListItem 
                  key={activity.id}
                  sx={{ 
                    borderLeft: `4px solid ${
                      activity.type === 'error' 
                        ? theme.palette.error.main 
                        : activity.type === 'warning'
                          ? theme.palette.warning.main
                          : theme.palette.success.main
                    }`,
                    mb: 1,
                    bgcolor: theme.palette.background.paper,
                    borderRadius: '0 4px 4px 0',
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: isMobile ? 'flex-start' : 'center'
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {getStatusIcon(activity.type)}
                  </ListItemIcon>
                  <ListItemText 
                    primary={activity.message}
                    secondary={new Date(activity.timestamp).toLocaleTimeString()}
                    primaryTypographyProps={{ 
                      variant: 'body2',
                      sx: { wordBreak: 'break-word' }
                    }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
              ))}
              
              {activities.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    No recent predictions
                  </Typography>
                </Box>
              )}
            </List>
          </Paper>
        </Grid>
        
        {/* League Performance Overview */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>League Performance</Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={6} sm={6} md={3}>
                <Box sx={{ textAlign: 'center', p: isMobile ? 1 : 2 }}>
                  <Avatar 
                    sx={{ 
                      width: isMobile ? 40 : 60, 
                      height: isMobile ? 40 : 60, 
                      mb: 1,
                      margin: '0 auto',
                      bgcolor: theme.palette.primary.light
                    }}
                  >
                    EPL
                  </Avatar>
                  <Typography variant={isMobile ? "subtitle2" : "h6"}>Premier League</Typography>
                  <Typography variant={isMobile ? "h6" : "h5"} color="primary" sx={{ mt: 1 }}>
                    82.4%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Prediction Accuracy
                  </Typography>
                  <Chip 
                    label="+3.2% vs last week" 
                    color="success" 
                    size="small" 
                    sx={{ mt: 1 }} 
                  />
                </Box>
              </Grid>
              
              <Grid item xs={6} sm={6} md={3}>
                <Box sx={{ textAlign: 'center', p: isMobile ? 1 : 2 }}>
                  <Avatar 
                    sx={{ 
                      width: isMobile ? 40 : 60, 
                      height: isMobile ? 40 : 60, 
                      mb: 1,
                      margin: '0 auto',
                      bgcolor: theme.palette.secondary.light
                    }}
                  >
                    BUN
                  </Avatar>
                  <Typography variant={isMobile ? "subtitle2" : "h6"}>Bundesliga</Typography>
                  <Typography variant={isMobile ? "h6" : "h5"} color="secondary" sx={{ mt: 1 }}>
                    78.1%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Prediction Accuracy
                  </Typography>
                  <Chip 
                    label="-1.5% vs last week" 
                    color="error" 
                    size="small" 
                    sx={{ mt: 1 }} 
                  />
                </Box>
              </Grid>
              
              <Grid item xs={6} sm={6} md={3}>
                <Box sx={{ textAlign: 'center', p: isMobile ? 1 : 2 }}>
                  <Avatar 
                    sx={{ 
                      width: isMobile ? 40 : 60, 
                      height: isMobile ? 40 : 60, 
                      mb: 1,
                      margin: '0 auto',
                      bgcolor: theme.palette.warning.light
                    }}
                  >
                    SER
                  </Avatar>
                  <Typography variant={isMobile ? "subtitle2" : "h6"}>Serie A</Typography>
                  <Typography variant={isMobile ? "h6" : "h5"} color="warning.dark" sx={{ mt: 1 }}>
                    75.3%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Prediction Accuracy
                  </Typography>
                  <Chip 
                    label="+0.8% vs last week" 
                    color="success" 
                    size="small" 
                    sx={{ mt: 1 }} 
                  />
                </Box>
              </Grid>
              
              <Grid item xs={6} sm={6} md={3}>
                <Box sx={{ textAlign: 'center', p: isMobile ? 1 : 2 }}>
                  <Avatar 
                    sx={{ 
                      width: isMobile ? 40 : 60, 
                      height: isMobile ? 40 : 60, 
                      mb: 1,
                      margin: '0 auto',
                      bgcolor: theme.palette.info.light
                    }}
                  >
                    LAL
                  </Avatar>
                  <Typography variant={isMobile ? "subtitle2" : "h6"}>La Liga</Typography>
                  <Typography variant={isMobile ? "h6" : "h5"} color="info.dark" sx={{ mt: 1 }}>
                    79.6%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Prediction Accuracy
                  </Typography>
                  <Chip 
                    label="+1.2% vs last week" 
                    color="success" 
                    size="small" 
                    sx={{ mt: 1 }} 
                  />
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardHome;
